/**
 * Two-band geo pricing for Light, Lern (Solo), and Edu (teacher) plans.
 *
 * Yearly = default checkout (billed once per year).
 * Monthly = secondary tab; priced higher so yearly is the better deal (~50% off at tier 1).
 *
 * Tier assignment: ISO 3166-1 alpha-2 billing / geo country codes.
 * Static HTML is prebaked as tier 1. Once a country is known, unlisted countries resolve to tier 2.
 *
 * Microstates & low-traffic territories (BM, KY, …) are assigned by GDP/capita band:
 * tier 1 ≈ high-income Western (~≥$50k PPP), tier 2 ≈ upper-middle / small rich (~$25–50k).
 *
 * Display amounts are synced from Stripe at build time into
 * `pricing-tier-amounts.generated.js` (see scripts/sync-stripe-paywall-prices.mjs).
 */

import generatedPricingAmounts from "./pricing-tier-amounts.generated.js";

/** @typedef {1 | 2} PricingTierId */

/**
 * @typedef {object} PricingTierAmounts
 * @property {string} label
 * @property {number} lightYearly Billed yearly (EUR)
 * @property {number} lernYearly Billed yearly (EUR)
 * @property {number} eduYearly
 * @property {number} lightMonthly Billed monthly (EUR)
 * @property {number} lernMonthly Billed monthly (EUR)
 * @property {number} eduMonthly
 */

/** @type {Record<PricingTierId, PricingTierAmounts>} */
const PRICING_TIER_AMOUNTS_FALLBACK = {
  1: {
    label: "Western / high-income",
    lightYearly: 36,
    lernYearly: 60,
    eduYearly: 120,
    lightMonthly: 5,
    lernMonthly: 10,
    eduMonthly: 20,
  },
  2: {
    label: "Rest of world",
    lightYearly: 24,
    lernYearly: 36,
    eduYearly: 60,
    lightMonthly: 5,
    lernMonthly: 9,
    eduMonthly: 15,
  },
};

/**
 * @param {number | undefined} generated
 * @param {number} fallback
 */
function pickPricingAmount(generated, fallback) {
  return typeof generated === "number" && generated > 0 ? generated : fallback;
}

/**
 * @returns {Record<PricingTierId, PricingTierAmounts>}
 */
function buildPricingTierAmounts() {
  const generated = generatedPricingAmounts?.amounts || {};
  /** @type {Record<PricingTierId, PricingTierAmounts>} */
  const out = {};
  for (const id of /** @type {PricingTierId[]} */ ([1, 2])) {
    const fallback = PRICING_TIER_AMOUNTS_FALLBACK[id];
    const fromGenerated = generated[String(id)] || {};
    out[id] = {
      label: fallback.label,
      lightMonthly: pickPricingAmount(fromGenerated.lightMonthly, fallback.lightMonthly),
      lightYearly: pickPricingAmount(fromGenerated.lightYearly, fallback.lightYearly),
      lernMonthly: pickPricingAmount(fromGenerated.lernMonthly, fallback.lernMonthly),
      lernYearly: pickPricingAmount(fromGenerated.lernYearly, fallback.lernYearly),
      eduMonthly: pickPricingAmount(fromGenerated.eduMonthly, fallback.eduMonthly),
      eduYearly: pickPricingAmount(fromGenerated.eduYearly, fallback.eduYearly),
    };
  }
  return out;
}

/** @type {Record<PricingTierId, PricingTierAmounts>} */
export const PRICING_TIER_AMOUNTS = buildPricingTierAmounts();

/**
 * Edu is a Stripe package price: the quantity is student licences and each started 50
 * is billed at the Edu amount, so 500 licences cost ten times the 50-licence price.
 */
export const EDU_INCLUDED_SEATS = 50;
/** Edu package sizes offered on the paywall, in switch order. */
export const EDU_SEAT_OPTIONS = /** @type {const} */ ([50, 500]);
/** Package the EDU card opens on. */
export const EDU_DEFAULT_SEATS = 500;

/**
 * Billed packages of 50 for a licence count (500 → 10).
 * @param {number | string | null | undefined} seats
 */
export function eduPackagesForSeats(seats) {
  const n = Number(seats);
  if (!Number.isFinite(n) || n <= EDU_INCLUDED_SEATS) return 1;
  return Math.ceil(n / EDU_INCLUDED_SEATS);
}

/**
 * Edu price for a licence count.
 * @param {PricingTierAmounts} amounts
 * @param {"monthly" | "yearly"} period
 * @param {number} [seats]
 */
export function eduPackageAmount(amounts, period, seats = EDU_INCLUDED_SEATS) {
  return (period === "monthly" ? amounts.eduMonthly : amounts.eduYearly) * eduPackagesForSeats(seats);
}

/**
 * Monthly equivalent of a yearly price (for “€X/mo” display).
 * @param {number} yearlyEur
 * @returns {number}
 */
export function yearlyToMonthlyDisplay(yearlyEur) {
  return yearlyEur / 12;
}

/**
 * “Save X%” vs paying monthly for 12 months. Floored so a 2/3 saving is 66, not 67.
 * @param {number} monthlyEur
 * @param {number} yearlyEur
 * @returns {number} 0–100 integer
 */
export function yearlySavingsPercent(monthlyEur, yearlyEur) {
  const fullYearMonthly = monthlyEur * 12;
  if (fullYearMonthly <= 0) return 0;
  return Math.floor((1 - yearlyEur / fullYearMonthly) * 100);
}

/**
 * @param {PricingTierAmounts} amounts
 * @returns {PricingTierAmounts & {
 *   lightYearlyMonthlyDisplay: number;
 *   lernYearlyMonthlyDisplay: number;
 *   eduYearlyMonthlyDisplay: number;
 *   lightYearlySavingsPercent: number;
 *   lernYearlySavingsPercent: number;
 *   eduYearlySavingsPercent: number;
 *   eduToLernYearlyRatio: number;
 * }}
 */
export function withPricingDisplayFields(amounts) {
  return {
    ...amounts,
    lightYearlyMonthlyDisplay: yearlyToMonthlyDisplay(amounts.lightYearly),
    lernYearlyMonthlyDisplay: yearlyToMonthlyDisplay(amounts.lernYearly),
    eduYearlyMonthlyDisplay: yearlyToMonthlyDisplay(amounts.eduYearly),
    lightYearlySavingsPercent: yearlySavingsPercent(amounts.lightMonthly, amounts.lightYearly),
    lernYearlySavingsPercent: yearlySavingsPercent(amounts.lernMonthly, amounts.lernYearly),
    eduYearlySavingsPercent: yearlySavingsPercent(amounts.eduMonthly, amounts.eduYearly),
    eduToLernYearlyRatio: amounts.eduYearly / amounts.lernYearly,
  };
}

/** @type {Record<string, PricingTierId>} */
export const PRICING_TIER_BY_COUNTRY = {
  // Tier 1 — Western / high-income (former €60 Lern / €96 Edu band)
  AD: 1,
  AE: 1,
  AT: 1,
  AU: 1,
  AX: 1,
  BE: 1,
  BM: 1,
  CA: 1,
  CH: 1,
  DE: 1,
  DK: 1,
  FI: 1,
  FO: 1,
  FR: 1,
  GB: 1,
  GI: 1,
  GG: 1,
  GL: 1,
  IE: 1,
  IM: 1,
  IS: 1,
  JE: 1,
  JP: 1,
  KY: 1,
  LI: 1,
  LU: 1,
  MC: 1,
  MT: 1,
  NL: 1,
  NO: 1,
  NZ: 1,
  SE: 1,
  US: 1,
  VA: 1,
  VG: 1,

};

/** Geo band for a known country that is not in {@link PRICING_TIER_BY_COUNTRY}. */
export const DEFAULT_PRICING_TIER = 2;

/**
 * Prices written into static HTML before the country header is read.
 * Tier 1 visitors then stay put; everyone else is corrected by the existing header lookup.
 */
export const PREBAKED_PRICING_TIER = 1;

const DEFAULT_TIER = DEFAULT_PRICING_TIER;

/**
 * @param {string | null | undefined} countryCode ISO 3166-1 alpha-2
 * @returns {PricingTierId}
 */
export function resolvePricingTier(countryCode) {
  const code = String(countryCode || "")
    .trim()
    .toUpperCase();
  if (!code) return PREBAKED_PRICING_TIER;
  return PRICING_TIER_BY_COUNTRY[code] || DEFAULT_TIER;
}

/**
 * @param {string | null | undefined} countryCode
 * @returns {typeof PRICING_TIER_AMOUNTS[PricingTierId]}
 */
export function resolvePricingTierAmounts(countryCode) {
  return PRICING_TIER_AMOUNTS[resolvePricingTier(countryCode)];
}

/**
 * Tier amounts plus display helpers (€/mo equiv, savings %, Edu÷Lern).
 * @param {string | null | undefined} countryCode
 */
export function resolvePricingTierDisplay(countryCode) {
  return withPricingDisplayFields(resolvePricingTierAmounts(countryCode));
}

function countriesForTier(tier) {
  return Object.entries(PRICING_TIER_BY_COUNTRY)
    .filter(([, t]) => t === tier)
    .map(([c]) => c)
    .sort();
}

/** Country codes per tier (sorted). */
export const PRICING_TIER_COUNTRY_LISTS = {
  1: countriesForTier(1),
  2: countriesForTier(2),
};
